import React, { useState, useEffect } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { LogOut, Bell, User, Mail, UserPlus, GraduationCap, Users, Award } from 'lucide-react';
import { toast } from 'sonner';
import MobileParentRegistration from './MobileParentRegistration';
import MobileAthleteRegistration from './MobileAthleteRegistration';
import MobileCoachRegistration from './MobileCoachRegistration';
import {
  registerPushNotifications,
  unregisterPushNotifications,
  subscribePushDiagnostics,
  getPushDiagnostics,
  type PushDiagnostics,
} from '../lib/pushRegistration';
import { getAppVersionInfo, type AppVersionInfo } from '../lib/appVersion';


type RegistrationScreen = 'none' | 'parent' | 'athlete' | 'coach';

interface ProfileScreenProps {
  user: SupabaseUser | null;
  profile: {
    id: string;
    email?: string;
    name?: string;
  } | null;
  onSignOut: () => Promise<void>;
  onLogin: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
}

const ProfileScreen: React.FC<ProfileScreenProps> = ({ user, profile, onSignOut, onLogin }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [registrationScreen, setRegistrationScreen] = useState<RegistrationScreen>('none');
  const [push, setPush] = useState<PushDiagnostics>(() => getPushDiagnostics());
  const [appVersion, setAppVersion] = useState<AppVersionInfo | null>(null);

  // Login form state (for non-authenticated view)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => subscribePushDiagnostics(setPush), []);
  useEffect(() => {
    getAppVersionInfo().then(setAppVersion);
  }, []);

  const handleNotificationsToggle = async (on: boolean) => {
    setNotificationsEnabled(on);
    if (on) {
      await registerPushNotifications({ prompt: true });
    } else {
      await unregisterPushNotifications();
    }
  };

  const permissionLabel =
    push.permission === 'prompt' || push.permission === 'prompt-with-rationale'
      ? 'not asked yet'
      : push.permission;


  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await onSignOut();
      toast.success('Signed out successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign out');
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const result = await onLogin(email, password);
      if (!result.success) {
        toast.error(result.error || 'Failed to sign in');
      } else {
        toast.success('Welcome back!');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleRegistrationSuccess = () => {
    setRegistrationScreen('none');
    toast.success('Account created! You are now signed in.');
  };

  const getInitials = () => {
    if (profile?.name) {
      return profile.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Show registration screen if selected
  if (registrationScreen === 'parent') {
    return (
      <MobileParentRegistration
        onSuccess={handleRegistrationSuccess}
        onBack={() => setRegistrationScreen('none')}
      />
    );
  }

  if (registrationScreen === 'athlete') {
    return (
      <MobileAthleteRegistration
        onSuccess={handleRegistrationSuccess}
        onBack={() => setRegistrationScreen('none')}
      />
    );
  }

  if (registrationScreen === 'coach') {
    return (
      <MobileCoachRegistration
        onSuccess={handleRegistrationSuccess}
        onBack={() => setRegistrationScreen('none')}
      />
    );
  }

  // NOT LOGGED IN VIEW
  if (!user) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <header className="sticky top-0 bg-background border-b border-border z-10">
          <div className="flex items-center px-4 h-14">
            <h1 className="text-lg font-semibold">Account</h1>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Sign In Section */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4">Sign In</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full mt-1 px-3 py-2.5 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="text-sm font-medium text-muted-foreground">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full mt-1 px-3 py-2.5 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoggingIn}>
                {isLoggingIn ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-background text-muted-foreground">
                or create an account
              </span>
            </div>
          </div>

          {/* Create Account Section */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Create Account</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Choose your role to get started
            </p>
            
            <div className="space-y-3">
              {/* Parent Option */}
              <button
                onClick={() => setRegistrationScreen('parent')}
                className="w-full flex items-center gap-4 p-4 bg-card border border-border rounded-xl hover:bg-muted/50 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">I'm a Parent</h3>
                  <p className="text-sm text-muted-foreground">Support your athlete's journey</p>
                </div>
              </button>

              {/* Athlete Option */}
              <button
                onClick={() => setRegistrationScreen('athlete')}
                className="w-full flex items-center gap-4 p-4 bg-card border border-border rounded-xl hover:bg-muted/50 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <GraduationCap className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">I'm an Athlete</h3>
                  <p className="text-sm text-muted-foreground">Create your recruiting profile</p>
                </div>
              </button>

              {/* Coach Option */}
              <button
                onClick={() => setRegistrationScreen('coach')}
                className="w-full flex items-center gap-4 p-4 bg-card border border-border rounded-xl hover:bg-muted/50 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Award className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">I'm a Coach</h3>
                  <p className="text-sm text-muted-foreground">Manage teams and discover talent</p>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 text-center text-xs text-muted-foreground">
          <p>
            RecruitLook Events
            {appVersion ? ` ${appVersion.label}` : ''}
          </p>
          <p className="mt-1">© {new Date().getFullYear()} RecruitLook</p>
        </div>
      </div>
    );
  }

  // LOGGED IN VIEW
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="sticky top-0 bg-background border-b border-border z-10">
        <div className="flex items-center px-4 h-14">
          <h1 className="text-lg font-semibold">Profile</h1>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Profile Card */}
        <div className="p-6 flex flex-col items-center border-b border-border">
          <Avatar className="h-20 w-20 mb-4">
            <AvatarFallback className="bg-primary text-primary-foreground text-xl">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <h2 className="text-xl font-semibold text-foreground">
            {profile?.name || 'User'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.email}
          </p>
        </div>

        {/* Settings List */}
        <div className="divide-y divide-border">
          {/* Account Info */}
          <div className="p-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Account</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <User className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Name</p>
                  <p className="text-sm text-muted-foreground">{profile?.name || 'Not set'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="p-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Preferences</h3>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">Push Notifications</p>
                  <p className="text-xs text-muted-foreground">Get alerts for scores & updates</p>
                </div>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={handleNotificationsToggle}
              />
            </div>

            {/* Temporary diagnostic — remove once push is confirmed working */}
            <div className="mt-2 rounded-md bg-muted/50 p-3 space-y-1 text-[11px] leading-snug text-muted-foreground">
              <p>Running in the app: {push.native ? 'yes' : 'no (browser)'}</p>
              <p>Signed in: {push.session}</p>
              <p>Permission: {permissionLabel}</p>
              <p>
                Asked Apple for a token: {push.registerCalled ? 'yes' : 'no'}
                {' · '}
                Token received: {push.tokenReceived ? `yes (${push.tokenPreview}…)` : 'no'}
              </p>
              <p>
                Saved: {push.writeStatus === 'ok' ? 'yes' : push.writeStatus}
                {push.writeError ? ` — ${push.writeError}` : ''}
              </p>
              {push.registrationError && <p>Apple said: {push.registrationError}</p>}
              <p>Last step: {push.lastStep}</p>
            </div>
          </div>


          {/* Sign Out */}
          <div className="p-4">
            <Button
              variant="destructive"
              className="w-full"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {isSigningOut ? 'Signing out...' : 'Sign Out'}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 text-center text-xs text-muted-foreground">
          <p>
            RecruitLook Events
            {appVersion ? ` ${appVersion.label}` : ''}
          </p>
          <p className="mt-1">© {new Date().getFullYear()} RecruitLook</p>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
